/**
 * ScaffoldCartBadge — AppShell 헤더 담기 카트 배지 (queue #682 · docs/biz-specs/project-scaffold.md
 * §UAT-2026-07-14-R690, 원안 queue #682 §UAT-2026-07-14-R682 §R682-4)
 *
 * `useScaffoldCart()` 의 count 를 구독해 count===0 이면 미렌더(숨김), count>0 이면 📦 아이콘 +
 * 개수 뱃지를 노출한다. `AuthLayout.tsx` headerActions 슬롯에 검색 버튼과 나란히 주입된다.
 *
 * queue #690 §UAT-2026-07-14-R690 — 배지 클릭은 더 이상 즉시 `/scaffold` 로 이동하지 않고
 * 경량 팝오버(role="dialog" · 비모달)를 토글한다. 팝오버는 담긴 코드+명칭 목록(코드→명칭은
 * `showcaseApi.getModules()` 를 `useSearchIndex` 와 동일 queryKey 로 재사용 — 실패/미해석
 * 시 코드만 표시)과 행별 ✕ 제거·'비우기'·'스캐폴드로 이동' CTA 를 제공한다. 이동은 CTA 가
 * 전담하며 기존 진입 경로(navigate('/scaffold'))는 보존된다. ESC/외부 클릭 닫힘 + 포커스
 * 트랩 + 닫힘 시 배지로 포커스 복귀. 마지막 1건 제거/비우기로 count 가 0 이 되면(배지 자체가
 * 언마운트) 팝오버를 먼저 닫고 헤더 검색 버튼(`[data-tour="global-search"]`)으로 포커스를
 * 이관해 포커스 유실을 막는다(§R690-2(c)).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useScaffoldCart } from '../hooks/useScaffoldCart';
import { showcaseApi } from '../../common-showcase/api/showcaseApi';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** 명칭 해석 실패/미해석 시 코드만 표시 — §R690-2(b) */
const LAZY_STALE_TIME_MS = 5 * 60 * 1000;

export function ScaffoldCartBadge() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { codes, count, remove, clear } = useScaffoldCart();
  const [open, setOpen] = useState(false);
  const badgeRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(count);

  // §R690-2(b) 명칭 해석 — `useSearchIndex.ts` 와 동일 queryKey 로 재사용(캐시 공유 · 신규 요청 0).
  const modulesQuery = useQuery({
    queryKey: ['_shared', 'search-index', 'capability'] as const,
    queryFn: () => showcaseApi.getModules(),
    enabled: open,
    staleTime: LAZY_STALE_TIME_MS,
  });

  const isEn = (i18n?.language ?? 'ko').toLowerCase().startsWith('en');
  const nameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of modulesQuery.data?.nodes ?? []) {
      const name = isEn && node.nameEn?.trim() ? node.nameEn : node.nameKo;
      if (node.code && name) map.set(node.code, name);
    }
    return map;
  }, [modulesQuery.data, isEn]);

  // §R690-2(c) 0건 전이 — 팝오버가 열린 채로 count 가 0 이 되면 닫고 포커스를 헤더 검색
  // 버튼으로 이관한다(배지 자체가 언마운트되어 포커스가 document.body 로 떨어지는 것을 방지).
  useEffect(() => {
    if (open && prevCountRef.current > 0 && count === 0) {
      setOpen(false);
      document.querySelector<HTMLElement>('[data-tour="global-search"]')?.focus();
    }
    prevCountRef.current = count;
  }, [count, open]);

  // 오픈 시 팝오버 첫 인터랙티브 요소로 포커스 이동
  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => {
      popoverRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // ESC 닫힘 + 포커스 트랩(Tab/Shift+Tab 순환) + 외부 클릭 닫힘
  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        badgeRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !popoverRef.current) return;
      const focusable = popoverRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || badgeRef.current?.contains(target)) return;
      setOpen(false);
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  if (count === 0) return null;

  function handleGoScaffold() {
    setOpen(false);
    navigate('/scaffold');
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={badgeRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('showcase.cart.badge.aria', { count })}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="scaffold-cart-badge"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-sm)',
          padding: '4px var(--spacing-2)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          color: 'var(--color-brand-primary)',
        }}
      >
        <span aria-hidden="true">📦</span>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {count}
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-modal="false"
          aria-label={t('showcase.cart.popover.aria')}
          data-testid="scaffold-cart-popover"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 1000,
            width: 320,
            maxWidth: 'calc(100vw - var(--spacing-4) * 2)',
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            padding: 'var(--spacing-3)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.cart.popover.title', { count })}
          </div>

          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: 320,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-1)',
            }}
          >
            {codes.map((code) => {
              const name = nameByCode.get(code);
              return (
                <li
                  key={code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-1) 0',
                    borderBottom: '1px solid var(--color-surface-border)',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--color-brand-primary)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 700,
                      }}
                    >
                      {code}
                    </span>
                    {name && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {name}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(code)}
                    aria-label={t('showcase.cart.popover.remove', { code })}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-sm)',
                      flexShrink: 0,
                      lineHeight: 1,
                      padding: 'var(--spacing-1)',
                    }}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--spacing-2)',
              marginTop: 'var(--spacing-3)',
            }}
          >
            <button
              type="button"
              onClick={() => clear()}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-surface-border)',
                background: 'var(--color-surface-background)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              {t('showcase.cart.popover.clear')}
            </button>
            <button
              type="button"
              onClick={handleGoScaffold}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
              }}
            >
              {t('showcase.cart.popover.goScaffold')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
